import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Settings, CheckCircle, AlertCircle, Save, Bell, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Toaster, toast } from 'sonner';
interface GatewayConfig {
  id: string;
  name: string;
  enabled: boolean;
  fields: {
    [key: string]: string;
  };
}
interface ReminderSettings {
  enabled: boolean;
  firstReminderDays: number;
  secondReminderDays: number;
  finalReminderDays: number;
}
const defaultGateways: GatewayConfig[] = [{
  id: 'stripe',
  name: 'Stripe',
  enabled: false,
  fields: {
    publishableKey: '',
    secretKey: '',
    webhookSecret: ''
  }
}, {
  id: 'paypal',
  name: 'PayPal',
  enabled: false,
  fields: {
    clientId: '',
    secret: '',
    sandbox: 'true'
  }
}, {
  id: 'square',
  name: 'Square',
  enabled: false,
  fields: {
    applicationId: '',
    accessToken: '',
    locationId: ''
  }
}, {
  id: 'authorize',
  name: 'Authorize.net',
  enabled: false,
  fields: {
    apiLoginId: '',
    transactionKey: ''
  }
}, {
  id: 'mollie',
  name: 'Mollie',
  enabled: false,
  fields: {
    apiKey: ''
  }
}, {
  id: 'razorpay',
  name: 'Razorpay',
  enabled: false,
  fields: {
    keyId: '',
    keySecret: ''
  }
}];
const defaultReminders: ReminderSettings = {
  enabled: true,
  firstReminderDays: 7,
  secondReminderDays: 3,
  finalReminderDays: 1
};
export function PaymentGatewayManager() {
  const [gateways, setGateways] = useState<GatewayConfig[]>(defaultGateways);
  const [reminders, setReminders] = useState<ReminderSettings>(defaultReminders);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    async function loadSettings() {
      try {
        const {
          data,
          error
        } = await supabase.from('site_settings').select('key, value').in('key', ['payment_gateways', 'payment_reminders']);
        if (error) throw error;
        if (data) {
          const gatewaysRow = data.find(r => r.key === 'payment_gateways');
          if (gatewaysRow && gatewaysRow.value) {
            // Merge saved with defaults to ensure all gateways exist
            const saved = gatewaysRow.value as GatewayConfig[];
            setGateways(defaultGateways.map(dg => saved.find(s => s.id === dg.id) || dg));
          }
          const remindersRow = data.find(r => r.key === 'payment_reminders');
          if (remindersRow && remindersRow.value) {
            setReminders(remindersRow.value as ReminderSettings);
          }
        }
      } catch (err) {
        console.error('Failed to load payment settings:', err);
        toast.error('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    }
    loadSettings();
  }, []);
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const {
        error: gwError
      } = await supabase.from('site_settings').upsert({
        key: 'payment_gateways',
        value: gateways,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });
      if (gwError) throw gwError;
      const {
        error: remError
      } = await supabase.from('site_settings').upsert({
        key: 'payment_reminders',
        value: reminders,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });
      if (remError) throw remError;
      toast.success('Payment settings saved successfully');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };
  const updateGatewayField = (id: string, field: string, value: string | boolean) => {
    setGateways(prev => prev.map(gw => {
      if (gw.id !== id) return gw;
      if (field === 'enabled') {
        return {
          ...gw,
          enabled: value as boolean
        };
      }
      return {
        ...gw,
        fields: {
          ...gw.fields,
          [field]: value as string
        }
      };
    }));
  };
  const testConnection = (name: string) => {
    toast.promise(new Promise(resolve => setTimeout(resolve, 1500)), {
      loading: `Testing connection to ${name}...`,
      success: `Successfully connected to ${name}!`,
      error: `Failed to connect to ${name}`
    });
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-id="element-952">
        <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-953" />
      </div>;
  }
  return <div className="space-y-8 pb-24" data-id="element-954">
      <Toaster theme="dark" position="top-right" data-id="element-955" />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-956">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-957">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-958">
            Payment Gateways
          </h1>
          <p className="text-slate-400" data-id="element-959">
            Configure payment processors and automated billing reminders.
          </p>
        </motion.div>

        <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-2 px-6 py-2.5 bg-fox-600 hover:bg-fox-500 disabled:opacity-50 text-white rounded-lg font-medium transition-colors shadow-[0_0_15px_rgba(234,88,12,0.2)]" data-id="element-960">
          <Save size={18} data-id="element-961" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-962">
        {gateways.map((gateway, i) => <motion.div key={gateway.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className={`bg-slate-950 border rounded-2xl overflow-hidden transition-colors ${gateway.enabled ? 'border-fox-500/50' : 'border-slate-800'}`} data-id="element-963">
            <div className="p-6 flex items-center justify-between cursor-pointer hover:bg-slate-900/50 transition-colors" onClick={() => setExpandedId(expandedId === gateway.id ? null : gateway.id)} data-id="element-964">
              <div className="flex items-center gap-4" data-id="element-965">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${gateway.enabled ? 'bg-fox-500/10 text-fox-500' : 'bg-slate-900 text-slate-500'}`} data-id="element-966">
                  <CreditCard size={24} data-id="element-967" />
                </div>
                <div data-id="element-968">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-969">
                    {gateway.name}
                    {gateway.enabled && <CheckCircle size={16} className="text-emerald-500" data-id="element-970" />}
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-971">
                    {gateway.enabled ? 'Active and ready' : 'Not configured'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-972">
                <label className="relative inline-flex items-center cursor-pointer" onClick={e => e.stopPropagation()} data-id="element-973">
                  <input type="checkbox" className="sr-only peer" checked={gateway.enabled} onChange={e => updateGatewayField(gateway.id, 'enabled', e.target.checked)} data-id="element-974" />
                  <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-fox-500" data-id="element-975"></div>
                </label>
                {expandedId === gateway.id ? <ChevronUp size={20} className="text-slate-500" data-id="element-976" /> : <ChevronDown size={20} className="text-slate-500" data-id="element-977" />}
              </div>
            </div>

            <AnimatePresence data-id="element-978">
              {expandedId === gateway.id && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden" data-id="element-979">
                  <div className="p-6 border-t border-slate-800 bg-slate-900/30 space-y-4" data-id="element-980">
                    {Object.entries(gateway.fields).map(([key, value]) => <div key={key} className="space-y-1.5" data-id="element-981">
                        <label className="text-sm font-medium text-slate-300 capitalize" data-id="element-982">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        {key === 'sandbox' ? <select value={value} onChange={e => updateGatewayField(gateway.id, key, e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-983">
                            <option value="true" data-id="element-984">Yes (Test Mode)</option>
                            <option value="false" data-id="element-985">No (Live Mode)</option>
                          </select> : <input type={key.toLowerCase().includes('secret') || key.toLowerCase().includes('token') ? 'password' : 'text'} value={value} onChange={e => updateGatewayField(gateway.id, key, e.target.value)} placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}`} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-986" />}
                      </div>)}

                    <div className="pt-4 flex justify-end" data-id="element-987">
                      <button onClick={() => testConnection(gateway.name)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-988">
                        Test Connection
                      </button>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>)}
      </div>

      {/* Payment Reminders Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 md:p-8 mt-8" data-id="element-989">
        <div className="flex items-center gap-3 mb-6" data-id="element-990">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center" data-id="element-991">
            <Bell size={20} data-id="element-992" />
          </div>
          <div data-id="element-993">
            <h2 className="text-xl font-bold text-white" data-id="element-994">
              Automated Payment Reminders
            </h2>
            <p className="text-sm text-slate-400" data-id="element-995">
              Configure when customers receive upcoming invoice emails.
            </p>
          </div>
        </div>

        <div className="space-y-6 max-w-2xl" data-id="element-996">
          <div className="flex items-center justify-between p-4 bg-slate-900 rounded-xl border border-slate-800" data-id="element-997">
            <div data-id="element-998">
              <h4 className="text-white font-medium" data-id="element-999">
                Enable Automated Reminders
              </h4>
              <p className="text-sm text-slate-400" data-id="element-1000">
                Send emails automatically before due dates
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-1001">
              <input type="checkbox" className="sr-only peer" checked={reminders.enabled} onChange={e => setReminders({
              ...reminders,
              enabled: e.target.checked
            })} data-id="element-1002" />
              <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500" data-id="element-1003"></div>
            </label>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 transition-opacity ${reminders.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-1004">
            <div className="space-y-2" data-id="element-1005">
              <label className="text-sm font-medium text-slate-300" data-id="element-1006">
                First Reminder
              </label>
              <div className="relative" data-id="element-1007">
                <input type="number" min="1" max="30" value={reminders.firstReminderDays} onChange={e => setReminders({
                ...reminders,
                firstReminderDays: parseInt(e.target.value) || 7
              })} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-4 pr-12 py-2.5 text-white focus:border-amber-500 focus:outline-none" data-id="element-1008" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-1009">
                  days
                </span>
              </div>
            </div>
            <div className="space-y-2" data-id="element-1010">
              <label className="text-sm font-medium text-slate-300" data-id="element-1011">
                Second Reminder
              </label>
              <div className="relative" data-id="element-1012">
                <input type="number" min="1" max="30" value={reminders.secondReminderDays} onChange={e => setReminders({
                ...reminders,
                secondReminderDays: parseInt(e.target.value) || 3
              })} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-4 pr-12 py-2.5 text-white focus:border-amber-500 focus:outline-none" data-id="element-1013" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-1014">
                  days
                </span>
              </div>
            </div>
            <div className="space-y-2" data-id="element-1015">
              <label className="text-sm font-medium text-slate-300" data-id="element-1016">
                Final Reminder
              </label>
              <div className="relative" data-id="element-1017">
                <input type="number" min="0" max="30" value={reminders.finalReminderDays} onChange={e => setReminders({
                ...reminders,
                finalReminderDays: parseInt(e.target.value) || 1
              })} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-4 pr-12 py-2.5 text-white focus:border-amber-500 focus:outline-none" data-id="element-1018" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-1019">
                  days
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl" data-id="element-1020">
            <AlertCircle className="text-blue-500 shrink-0 mt-0.5" size={18} data-id="element-1021" />
            <div className="text-sm text-blue-200" data-id="element-1022">
              <p className="font-medium mb-1" data-id="element-1023">
                Requires Edge Function Cron Job
              </p>
              <p className="text-blue-300/80" data-id="element-1024">
                To actually send these emails, you need to deploy the{' '}
                <code data-id="element-1025">send-payment-reminders</code> Edge Function and configure
                a pg_cron job in your Supabase database to run it daily.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}